import { computed } from '@angular/core';
import { patchState, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';
import { omit } from 'lodash';

export type SystemStatus = 'initial' | 'online' | 'offline' | 'partial';

export interface SystemStatusDetails {
  [key: string]: { operational: boolean; description: string };
}

function getStatus(details: SystemStatusDetails): SystemStatus {
  const keys = Object.keys(details);

  if (!keys.length) {
    return 'initial';
  }

  if (keys.every(key => details[key].operational)) {
    return 'online';
  }

  if (keys.every(key => !details[key].operational)) {
    return 'offline';
  }

  return 'partial';
}

export const useSystemStatus = () =>
  signalStoreFeature(
    withState({
      systemStatusDetails: {} as SystemStatusDetails,
    }),
    withComputed(store => {
      const systemStatus = computed<SystemStatus>(() => getStatus(store.systemStatusDetails()));

      const systemStatusDescription = computed(() => {
        const status = systemStatus();
        const details = store.systemStatusDetails();

        if (status === 'initial') {
          return 'Initialization';
        }

        return Object.values(details)
          .map(value => value.description)
          .join('\r\n');
      });

      return { systemStatus, systemStatusDescription };
    }),
    withMethods(store => {
      const updateSystemStatus = (key: string, operational: boolean, description: string) => {
        patchState(store, state => ({
          systemStatusDetails: {
            ...state.systemStatusDetails,
            [key]: { operational, description },
          },
        }));
      };

      const removeSystemStatus = (key: string) => {
        patchState(store, state => ({
          systemStatusDetails: omit(state.systemStatusDetails, [key]),
        }));
      };

      return { updateSystemStatus, removeSystemStatus };
    }),
  );
